/**
 * @file PayGroupMultiSelect.js
 * @description A multi-select dropdown component for selecting pay groups.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchPayGroups } from "../../../services/orgLevelService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * A multi-select component for selecting pay groups.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {Array} props.selectedPayGroups - The array of selected pay groups.
 * @param {Function} props.onPayGroupsChange - The function to handle pay group selection change.
 * @param {string} [props.label="Pay Groups"] - The label for the component.
 * @param {boolean} [props.isRequired=false] - Indicates if the component is required.
 * @param {boolean} props.resetTrigger - The trigger to reset the component.
 * @param {string|null} [props.tenantId=null] - The ID of the tenant.
 * @param {boolean} [props.showSelectedItemsInField=true] - Indicates if the selected items should be shown in the field.
 * @param {boolean} [props.showSelectionBox=true] - Indicates if the selection box should be shown.
 * @returns {JSX.Element} The rendered PayGroupMultiSelect component.
 */
const PayGroupMultiSelect = ({
  className,
  selectedPayGroups,
  onPayGroupsChange,
  label = "Pay Groups",
  isRequired = false,
  resetTrigger,
  tenantId = null,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [payGroups, setPayGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPayGroups = async () => {
      try {
        setIsLoading(true);
        const data = await fetchPayGroups(tenantId);
        setPayGroups(data);
      } catch (error) {
        console.error("Could not fetch pay groups:", error);
        Toast.error(getText('msg_86a287e5a578'));
      } finally {
        setIsLoading(false);
      }
    };

    loadPayGroups();
  }, [tenantId]);

  const handlePayGroupsChange = (selectedOptions) => {
    onPayGroupsChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="pay-group-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="pay-group-dropdown"
        options={payGroups}
        optionIDField="id"
        updateOptions={handlePayGroupsChange}
        savedOptions={selectedPayGroups}
        savedOptionIDField="id"
        disabled={payGroups.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_369d31346464')}
      />
    </div>
  );
};

export default PayGroupMultiSelect;
